import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";

// The portal's session credential is the bearer token from the link the sub-client arrived on,
// sent as a header on every call. No optional chaining anywhere in this pack: Node 12.
//
// It was originally held in memory alone. That could not survive a reload: the token is scrubbed
// from the address bar the moment it is read (decision 40), so after a refresh neither the URL nor
// the process had it and the sub-client was told their link was invalid while it was still live —
// mid-flow, on a page where people are entering card details.
//
// sessionStorage, deliberately, not localStorage. The only thing localStorage would add is
// surviving tab-close and browser restart, and it buys no capability: the fragment is already gone,
// so the recovery path after closing the tab is the email link, which still carries the token.
// Against that it is shared across every tab on this origin — the dashboard, the builder,
// ActiveAdmin — so an XSS anywhere in the app could lift a live billing credential, and on a shared
// machine the token would outlive the visit by up to EMAIL_TTL (72h). sessionStorage confines it to
// the tab that opened the link, and Chrome still restores it on session restore.
@Injectable()
export class BillingPortalService {
  static BASE = "/api/internal/billing_portal";
  // Namespaced: this origin's other apps use storage too.
  static STORAGE_KEY = "tr_billing_portal_session";

  private token: string = null;
  private expiresAt: string = null;

  constructor(private httpClient: HttpClient) {}

  setToken(token: string) {
    this.token = token;
    if (token) {
      this.persist();
    } else {
      // endSession() funnels through here, so a rejected token clears the stored copy too and a
      // reload cannot resurrect it.
      this.expiresAt = null;
      BillingPortalService.clearStored();
    }
  }

  // The link's own expiry, learned from the session payload. Stored alongside the token so a dead
  // credential expires itself rather than sitting there until the server happens to reject it.
  noteExpiry(expiresAt: string) {
    this.expiresAt = expiresAt || null;
    if (this.token) this.persist();
  }

  private persist() {
    try {
      window.sessionStorage.setItem(
        BillingPortalService.STORAGE_KEY,
        JSON.stringify({ token: this.token, expires_at: this.expiresAt })
      );
    } catch (_unused) {
      // Private mode / storage disabled: the in-memory token still serves this page view.
    }
  }

  // Restores the token after a reload. Returns null once past expiry, dropping the stored copy.
  static storedToken(): string {
    var raw: string = null;
    try {
      raw = window.sessionStorage.getItem(BillingPortalService.STORAGE_KEY);
    } catch (_unused) {
      return null;
    }
    if (!raw) return null;

    try {
      var saved = JSON.parse(raw);
      if (!saved || !saved.token) {
        BillingPortalService.clearStored();
        return null;
      }
      if (saved.expires_at && new Date(saved.expires_at).getTime() <= Date.now()) {
        BillingPortalService.clearStored();
        return null;
      }
      return saved.token;
    } catch (_unused) {
      BillingPortalService.clearStored();
      return null;
    }
  }

  static clearStored() {
    try {
      window.sessionStorage.removeItem(BillingPortalService.STORAGE_KEY);
    } catch (_unused) {}
  }

  // Reads #token=... from the URL fragment, where BillingPortal::MintLink puts it: a fragment is
  // never sent to a server, so the token stays out of every access log between the browser and
  // us. Exposed for tests.
  static tokenFromHash(hash: string): string {
    if (!hash) return null;
    var fragment = hash.charAt(0) === "#" ? hash.substring(1) : hash;
    var pairs = fragment.split("&");
    for (var i = 0; i < pairs.length; i++) {
      var parts = pairs[i].split("=");
      if (parts[0] === "token" && parts.length > 1) {
        return decodeURIComponent(parts.slice(1).join("="));
      }
    }
    return null;
  }

  // The Stripe publishable key comes from the layout's system meta tag, like the builder.
  static stripePublishableKey(): string {
    var meta = document.head.querySelector('meta[name="system"]');
    if (!meta) return null;
    try {
      var system = JSON.parse(meta.getAttribute("content"));
      return system && system.stripe_publishable_key ? system.stripe_publishable_key : null;
    } catch (_unused) {
      return null;
    }
  }

  private options(): { headers: HttpHeaders } {
    return { headers: new HttpHeaders({ Authorization: "Bearer " + (this.token || "") }) };
  }

  session(): Observable<any> {
    return this.httpClient.get<any>(BillingPortalService.BASE + "/session", this.options());
  }

  updateBillingContact(contact: { billing_email: string; billing_contact_name: string }): Observable<any> {
    return this.httpClient.patch<any>(BillingPortalService.BASE + "/billing_contact", { billing_contact: contact }, this.options());
  }

  paymentMethods(): Observable<any[]> {
    return this.httpClient.get<any[]>(BillingPortalService.BASE + "/payment_methods", this.options());
  }

  createPaymentMethod(type: string, data: any): Observable<any> {
    return this.httpClient.post<any>(BillingPortalService.BASE + "/payment_methods", { type: type, data: data }, this.options());
  }

  deletePaymentMethod(id: number): Observable<any> {
    return this.httpClient.delete<any>(BillingPortalService.BASE + "/payment_methods/" + id, this.options());
  }

  makeDefault(id: number): Observable<any> {
    return this.httpClient.post<any>(BillingPortalService.BASE + "/payment_methods/" + id + "/make_default", {}, this.options());
  }

  verifyMicroDeposits(id: number, payload: any): Observable<any> {
    return this.httpClient.post<any>(BillingPortalService.BASE + "/payment_methods/" + id + "/verify_micro_deposits", payload, this.options());
  }

  createSetupIntent(): Observable<{ client_secret: string; setup_intent_id: string }> {
    return this.httpClient.post<{ client_secret: string; setup_intent_id: string }>(BillingPortalService.BASE + "/setup_intents", {}, this.options());
  }

  invoices(): Observable<any[]> {
    return this.httpClient.get<any[]>(BillingPortalService.BASE + "/invoices", this.options());
  }

  invoice(id: number): Observable<any> {
    return this.httpClient.get<any>(BillingPortalService.BASE + "/invoices/" + id, this.options());
  }

  // Downloads go through fetch so the bearer header travels with them; a plain <a href>
  // would arrive unauthenticated.
  downloadInvoice(id: number): Promise<Blob> {
    return fetch(BillingPortalService.BASE + "/invoices/" + id + "/download", {
      headers: { Authorization: "Bearer " + (this.token || "") }
    }).then(function(response) {
      if (!response.ok) {
        // Carries the status so the portal can tell a dead link (401) from a failed download.
        var error: any = new Error("Download failed (" + response.status + ")");
        error.status = response.status;
        throw error;
      }
      return response.blob();
    });
  }
}
